/**
 * The leave prompts of the mounted surfaces holding unsaved edits, newest
 * last. `useUnsavedChangesGuard` registers one while its surface is dirty; a
 * surface hidden by Next's Activity cache runs its effect cleanup, so a
 * hidden draft is never asked about.
 *
 * A plain module, apart from the hook: the always-mounted chrome that asks
 * it (the brand switcher, the organization switch, the command palette,
 * notifications, chat navigation pills) must not pull the hook and its
 * dialog wiring into every route's client bundle.
 */
const leaveRequests: Array<() => Promise<boolean>> = []

/** Registers a surface's leave prompt; the returned function removes it. */
export function registerLeaveRequest(
  requestLeave: () => Promise<boolean>
): () => void {
  leaveRequests.push(requestLeave)
  return () => {
    const index = leaveRequests.indexOf(requestLeave)
    if (index >= 0) {
      leaveRequests.splice(index, 1)
    }
  }
}

/**
 * For an in-app action that replaces the surface WITHOUT a link — a brand
 * switch or Add brand, an organization switch, a command-palette jump, a
 * notification, a chat navigation pill:
 * resolves `true` when nothing is unsaved or once the person confirms
 * leaving, `false` when they stay (or a pending write holds the surface).
 * Links need none of this; the guard intercepts those itself.
 */
export function confirmLeaveIfUnsaved(): Promise<boolean> {
  const requestLeave = leaveRequests.at(-1)
  return requestLeave ? requestLeave() : Promise.resolve(true)
}

/** Back / Forward handlers of the dirty surfaces, newest last. */
const popstateGuards: Array<(event: PopStateEvent) => void> = []
let isPopstateInterceptorInstalled = false

/**
 * The one `popstate` listener the guards answer through. It must be
 * registered BEFORE the app router mounts — `instrumentation-client.ts` calls
 * this before hydration — because Chrome runs a window's listeners in
 * registration order whatever their capture flag (verified on the preview: a
 * capture listener the page added after the router's still ran second, by
 * which time the router had restored the destination and unmounted the
 * dirty surface). Registered first — and as a capture listener, for engines
 * that run those ahead at the target — a guard can stop the event before the
 * router sees it. Idempotent.
 */
export function installEarlyPopstateInterceptor(): void {
  if (isPopstateInterceptorInstalled || typeof window === 'undefined') {
    return
  }
  isPopstateInterceptorInstalled = true
  window.addEventListener(
    'popstate',
    (event) => {
      popstateGuards.at(-1)?.(event)
    },
    true
  )
}

/**
 * Registers a dirty surface's Back / Forward handler; the returned function
 * removes it. The handler may `stopImmediatePropagation()` to keep the
 * router from ever seeing the traversal.
 */
export function registerPopstateGuard(
  guard: (event: PopStateEvent) => void
): () => void {
  popstateGuards.push(guard)
  return () => {
    const index = popstateGuards.indexOf(guard)
    if (index >= 0) {
      popstateGuards.splice(index, 1)
    }
  }
}
